import { useCallback, useState } from 'react';

import { useNotification } from '../../../../../../../contexts/NotificationContext';
import {
  useSuppliersPageUpdateRequestToJoinMutation,
  type JoinRequestAllowedUpdateStatus,
} from '../../../../../../../generated';
import {
  describeJoinRequestOutcome,
  resolveJoinRequestUpdate,
} from '../../../../../utils/supplierJoinRequests';

export interface UseUpdateSupplierJoinRequestConfig {
  /** Refreshes the request list and the member list after a decision lands. */
  onRefetch?: () => Promise<unknown>;
}

export interface UseUpdateSupplierJoinRequestResult {
  decideJoinRequest: (
    requestId: string,
    status: JoinRequestAllowedUpdateStatus,
    userName: string
  ) => Promise<void>;
  /** The request currently being decided, or null when idle. */
  processingRequestId: string | null;
}

export const useUpdateSupplierJoinRequest = ({
  onRefetch,
}: UseUpdateSupplierJoinRequestConfig = {}): UseUpdateSupplierJoinRequestResult => {
  const { addNotification } = useNotification();
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(
    null
  );

  const [updateRequestToJoin] = useSuppliersPageUpdateRequestToJoinMutation();

  const decideJoinRequest = useCallback(
    async (
      requestId: string,
      status: JoinRequestAllowedUpdateStatus,
      userName: string
    ): Promise<void> => {
      setProcessingRequestId(requestId);

      try {
        // The client sets mutate.errorPolicy: 'all', so this call RESOLVES on
        // failure with `errors` populated instead of throwing. The catch below
        // only covers network-level faults — `resolveJoinRequestUpdate` is what
        // stops a failed write being reported as a success.
        const result = await updateRequestToJoin({
          variables: { input: { id: requestId, status } },
        });

        const outcome = resolveJoinRequestUpdate({
          data: result.data,
          errors: result.errors,
        });

        if (!outcome.ok) {
          addNotification({
            type: 'error',
            title: 'Request not updated',
            message: outcome.message,
            duration: 7000,
          });

          // Some refusals mean the row on screen is out of date: it was decided
          // elsewhere, or it is gone. Leaving it listed with live buttons
          // invites a second attempt at something that cannot succeed, so pull
          // the current state before handing the screen back.
          if (outcome.staleView) {
            await onRefetch?.().catch(() => undefined);
          }
          return;
        }

        const { title, message } = describeJoinRequestOutcome(
          outcome.status,
          userName
        );
        addNotification({ type: 'success', title, message, duration: 5000 });

        // Best-effort: the decision already landed, so a refetch failure must
        // not be reported as a failed decision.
        await onRefetch?.().catch(() => undefined);
      } catch {
        // A dropped connection does not say whether the write landed, so the
        // list has to be reread rather than assumed unchanged.
        addNotification({
          type: 'error',
          title: 'Request not updated',
          message: 'Check your connection, then check the list and try again.',
          duration: 7000,
        });
        await onRefetch?.().catch(() => undefined);
      } finally {
        setProcessingRequestId(null);
      }
    },
    [updateRequestToJoin, addNotification, onRefetch]
  );

  return { decideJoinRequest, processingRequestId };
};
